<template>
  <div class="login" v-if="!store.getters.isLoggedIn">
    <div class="login__container">
      <div class="login__title">{{ $t('login.log_in') }}</div>
      <div class="login__text text-capitalize-first">{{ $t('login.login_to_continue') }}</div>
      <form class="login__form">
        <input type="text" class="form-control login__input user-icon" v-model="username" :placeholder="$t('labels.login')">
        <input type="password" class="form-control login__input password-icon" v-model="password" :placeholder="$t('labels.password')">
        <vue-hcaptcha
          v-if="captchaSiteKey"
          ref="captcha"
          :sitekey="captchaSiteKey"
          :language="lang"
          @verify="onCaptchaVerify"
          @expired="captchaToken = null"
          @challenge-expired="captchaToken = null"
          @error="onCaptchaError"
        />
        <button
          type="button"
          @click.prevent="doLogin"
          class="btn btn--accept text-capitalize-first w-100"
          :disabled="!!captchaSiteKey && !captchaToken"
        >
          <span class=" text-capitalize-first">{{ $t('button.enter') }}</span>
        </button>
      </form>
    </div>
  </div>
</template>
<script lang="ts">
import {computed, defineComponent, nextTick, reactive, ref, toRefs} from 'vue'
import {$t, helper} from "@/service/CommonHelper";
import {useStore} from "vuex";
import {useNotification} from "@kyvg/vue3-notification";
import router from "@/apps/public/router";
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import {getCaptchaSiteKey, isCaptchaRequired} from "@/service/LoginCaptchaHelper";

export default defineComponent({
  components: {
    VueHcaptcha,
  },
  setup() {
    const notification = useNotification()
    const store = useStore()
    const captcha = ref<InstanceType<typeof VueHcaptcha> | null>(null)
    const state = reactive({
      username: '' as string,
      password: '' as string,
      captchaSiteKey: null as string | null,
      captchaToken: null as string | null,
    });

    const lang = computed(() => store.getters.lang || window.userLang || 'auto')

    const onCaptchaVerify = (token: string) => {
      state.captchaToken = token
    }

    const onCaptchaError = () => {
      state.captchaToken = null
      notification.notify({
        title: $t('login.errors.captcha_error'),
        type: 'warn'
      });
    }

    const doLogin = async () => {
      if (state.captchaSiteKey && !state.captchaToken) {
        notification.notify({
          title: $t('login.errors.captcha_required'),
          type: 'warn'
        });
        return;
      }

      try {
        await store.dispatch('login', {
          username: state.username,
          password: state.password,
          captchaToken: state.captchaToken || undefined,
        })
        state.captchaSiteKey = null
        state.captchaToken = null
        await store.dispatch('loadAppData');
        await store.dispatch('loadUser');
        router.push('/');
      } catch (error: any) {
        if (isCaptchaRequired(error)) {
          state.captchaSiteKey = getCaptchaSiteKey(error) || state.captchaSiteKey
          state.captchaToken = null
          await nextTick()
          captcha.value?.reset()
          return
        }

        if (error.response && error.response.status === 401) {
          notification.notify({
            title: $t('login.errors.invalid_credentials'),
            type: 'warn'
          });
        } else {
          notification.notify({
            title: $t('login.errors.auth_error'),
            type: 'warn'
          });
        }
      }
    }

    return {
      ...toRefs(state),
      helper,
      store,
      captcha,
      lang,
      doLogin,
      onCaptchaVerify,
      onCaptchaError,
    }
  }
})
</script>
